import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MasterSelect } from "@/components/MasterSelect";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";

const empty = {
  name: "", product_type: "shade_wise", print_name: "", company: "", quality: "",
  main_unit: "", alternative_unit: "", hsn_code: "", sales_price: "", purchase_price: "",
  min_stock: "", gst_pct: "",
};

const L = ({ children }) => (
  <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{children}</Label>
);

// Add an item without leaving the open voucher. onCreated(item) hands it back to the grid.
export const QuickItemDialog = ({ open, onOpenChange, onCreated, initialName = "" }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [masters, setMasters] = useState({ company: [], quality: [], main_unit: [], alt_unit: [] });

  useEffect(() => {
    if (!open) return;
    setForm({ ...empty, name: initialName });
    Promise.all(["company", "quality", "main_unit", "alt_unit"].map((t) => api.get(`/masters/${t}`)))
      .then(([c, q, mu, au]) =>
        setMasters({
          company: c.data.map((m) => m.name),
          quality: q.data.map((m) => m.name),
          main_unit: mu.data.map((m) => m.name),
          alt_unit: au.data.map((m) => m.name),
        })
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Item name is needed");
    setSaving(true);
    try {
      const { data } = await api.post("/items", {
        ...form,
        sales_price: form.sales_price === "" ? null : parseFloat(form.sales_price),
        purchase_price: form.purchase_price === "" ? null : parseFloat(form.purchase_price),
        min_stock: form.min_stock === "" ? 0 : parseFloat(form.min_stock),
        gst_pct: form.gst_pct === "" ? 0 : parseFloat(form.gst_pct),
      });
      toast.success("Item added");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-item-dialog" className="max-w-2xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tight">New item</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Saved straight into the Item Master — your voucher stays open behind this box.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="col-span-2 sm:col-span-2">
            <L>Item name</L>
            <Input autoFocus data-testid="quick-item-name" className="mt-1 rounded-sm" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <L>Product type</L>
            <select
              data-testid="quick-item-type"
              className="mt-1 h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
              value={form.product_type}
              onChange={set("product_type")}
            >
              <option value="shade_wise">Shade-wise</option>
              <option value="without_shade">Without shade</option>
              <option value="pcs">PCS</option>
            </select>
          </div>
          <div>
            <L>Company</L>
            <MasterSelect className="mt-1 rounded-sm" value={form.company} options={masters.company}
              onChange={(v) => setForm((f) => ({ ...f, company: v }))} testId="quick-item-company" />
          </div>
          <div>
            <L>Quality</L>
            <MasterSelect className="mt-1 rounded-sm" value={form.quality} options={masters.quality}
              onChange={(v) => setForm((f) => ({ ...f, quality: v }))} testId="quick-item-quality" />
          </div>
          <div>
            <L>Main unit</L>
            <MasterSelect className="mt-1 rounded-sm" value={form.main_unit} options={masters.main_unit}
              onChange={(v) => setForm((f) => ({ ...f, main_unit: v }))} testId="quick-item-main-unit" />
          </div>
          <div>
            <L>Alt. unit</L>
            <MasterSelect className="mt-1 rounded-sm" value={form.alternative_unit} options={masters.alt_unit}
              onChange={(v) => setForm((f) => ({ ...f, alternative_unit: v }))} testId="quick-item-alt-unit" />
          </div>
          <div>
            <L>HSN</L>
            <Input data-testid="quick-item-hsn" className="mt-1 rounded-sm" value={form.hsn_code} onChange={set("hsn_code")} />
          </div>
          <div>
            <L>GST %</L>
            <Input data-testid="quick-item-gst" className="mt-1 rounded-sm" value={form.gst_pct} onChange={set("gst_pct")} />
          </div>
          <div>
            <L>Sales price</L>
            <Input data-testid="quick-item-sales" className="mt-1 rounded-sm" value={form.sales_price} onChange={set("sales_price")} />
          </div>
          <div>
            <L>Purchase rate</L>
            <Input data-testid="quick-item-purchase" className="mt-1 rounded-sm" value={form.purchase_price} onChange={set("purchase_price")} />
          </div>
          <div className="col-span-2 mt-2 flex justify-end gap-2 sm:col-span-3">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}
              data-testid="quick-item-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-f2-save
              className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" data-testid="quick-item-save">
              {saving ? "Saving…" : "Save item"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
